import { VesselLeg } from "../hooks/usePrevetAPI"

/**
 * ECharts dispatches `click` for every interactive element in the chart, not only
 * for the speed data points. The speed chart also draws:
 *
 *  - the zones of concern / ocean zone `markArea` rectangles (series 0 and 2)
 *  - the leg separator `markLine`s (series 0)
 *  - the average and "USR VESSEL" `markLine`s (series 1 and 3)
 *
 * For those elements ECharts reports `componentType: "markArea" | "markLine"` and a
 * `dataIndex` that counts inside that marker's own data, plus a `data` object that
 * has no `pos_date`. Reading `speedData[dataIndex]` (or `event.data.pos_date`) for
 * such a click resolves to an unrelated leg - typically `speedData[0]`, the very
 * first point of the chart - which is why the Trade Map used to open on a date the
 * user never clicked.
 *
 * Only `componentType === "series"` carries a usable index: `getDataParams` returns
 * the raw data index there, so it stays correct even while the data zoom is active.
 * For every other click inside the plot we fall back to the category under the
 * pointer, which is exactly the point the axis tooltip was showing at click time.
 */
export function resolveClickedLeg(
  event: any,
  chart: any,
  speedData: VesselLeg[] | undefined
): VesselLeg | undefined {
  if (!speedData || speedData.length < 1) {
    return undefined
  }

  if (event?.componentType === "series") {
    const dataIndex = event.dataIndex
    if (
      typeof dataIndex === "number" &&
      dataIndex >= 0 &&
      dataIndex < speedData.length
    ) {
      return speedData[dataIndex]
    }
    // Defensive: a series click should always carry a raw index, but the dataset
    // item itself is just as authoritative.
    const posDate = (event.data as Partial<VesselLeg> | undefined)?.pos_date
    return posDate
      ? speedData.find((leg) => leg.pos_date === posDate)
      : undefined
  }

  return resolveLegUnderPointer(event, chart, speedData)
}

function resolveLegUnderPointer(
  event: any,
  chart: any,
  speedData: VesselLeg[]
): VesselLeg | undefined {
  const offsetX = event?.event?.offsetX
  const offsetY = event?.event?.offsetY

  if (
    !chart ||
    typeof chart.convertFromPixel !== "function" ||
    typeof offsetX !== "number"
  ) {
    return undefined
  }

  // markArea / markLine elements always live inside one of the two grids. Anything
  // outside them (title, data zoom slider, axis labels) is not a date selection.
  if (typeof chart.containPixel === "function" && typeof offsetY === "number") {
    const point = [offsetX, offsetY]
    const insideGrid =
      chart.containPixel({ gridIndex: 0 }, point) ||
      chart.containPixel({ gridIndex: 1 }, point)
    if (!insideGrid) {
      return undefined
    }
  }

  // The speed series sits on the category xAxis 0, whose ordinal numbers follow the
  // dataset order, so the ordinal number is the index into `speedData`.
  const converted = chart.convertFromPixel({ xAxisIndex: 0 }, offsetX)
  const ordinal = Array.isArray(converted) ? converted[0] : converted

  if (typeof ordinal !== "number" || !Number.isFinite(ordinal)) {
    return undefined
  }

  const index = Math.min(
    Math.max(Math.round(ordinal), 0),
    speedData.length - 1
  )
  return speedData[index]
}
